/* eslint-disable prettier/prettier */
'use client';

import { useRef, useState } from 'react';
import { Button } from 'app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'app/components/ui/dialog';
import { Label } from 'app/components/ui/label';
import { X, Upload } from 'lucide-react';
import { useMediaSlice, MediaAction } from 'app/pages/Media/slice';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { cn } from 'utils/shadcn';
import { useToast } from 'app/components/ui/use-toast';

interface DialogFileUploadProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  folder: string;
}

export function DialogFileUpload({
  isOpen,
  onOpenChange,
  folder,
}: DialogFileUploadProps) {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { useUploadMediaMutation } = useMediaSlice();
  const { toast } = useToast();

  const [
    uploadFile,
    {
      isLoading: isUploadingFile,
      error: fileUploadingError,
      data: fileUploaded,
    },
  ] = useUploadMediaMutation();

  const removeFile = () => {
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    dispatch(MediaAction.setUploadedFile(null));
  };

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    const formData = new FormData();
    formData.append(folder, file);

    try {
      const response = await uploadFile(formData).unwrap();
      console.log('Upload response:', response);

      if (response?.response?.[folder]?.[0]) {
        const uploadedFile = response.response[folder][0];
        setFilePreview(uploadedFile);

        // Dispatch to Redux store with the correct payload
        dispatch(
          MediaAction.setUploadedFile({
            key: uploadedFile,
            folder: folder,
          }),
        );

        toast({
          title: 'File uploaded successfully',
          description: 'File uploaded successfully',
          variant: 'success',
        });
        onOpenChange(false);
        setFilePreview(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      toast({
        title: 'Failed to upload file',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] rounded-lg">
        <DialogHeader>
          <DialogTitle>Upload Media</DialogTitle>
        </DialogHeader>
        <DialogDescription></DialogDescription>

        <div className="grid grid-cols-1 gap-6 px-5 py-3">
          <div className="space-y-3">
            <Label className="block text-sm font-medium text-gray-700">
              Upload File
            </Label>
            <div className="flex flex-col items-center">
              <div className="relative group">
                <div
                  className={cn(
                    `w-full h-32 border-2 border-dashed ${
                      filePreview ? 'border-transparent' : 'border-gray-300'
                    } flex items-center justify-center overflow-hidden bg-gray-50 rounded-lg`,
                  )}
                >
                  {filePreview ? (
                    <>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">File uploaded</p>
                        <p className="text-xs text-gray-500">{filePreview}</p>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="absolute top-2 right-2 p-1 bg-white  dark:bg-transparent rounded-full shadow-sm hover:bg-gray-100 transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-600" />
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col items-center p-4 text-center">
                      <span className="text-xs text-gray-500">
                        Upload files (max 10MB)
                      </span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  accept="image/*"
                />
                <Label
                  htmlFor="file-upload"
                  className={cn(
                    'mt-3 w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer',
                    isUploading && 'opacity-70',
                  )}
                >
                  {isUploadingFile ? (
                    'Uploading...'
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {filePreview ? 'Change File' : 'Upload File'}
                    </>
                  )}
                </Label>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
